import {
  Sanity$GridItem,
  Sanity$GridStylingOptions,
} from '@otovo/shared/types/sanityTypes';

import ArrowRight from '@otovo/shared/images/icons/arrow-right.svg';
import cx from 'classnames';

import ConditionalWrap from '@otovo/shared/components/ImageComponents/ConditionalWrap';
import SanityImage from '@otovo/shared/components/Sanity/SanityImage';

import PortableText from '@otovo/shared/components/Sanity/PortableTextComponents';
import { stegaClean } from 'next-sanity';

type Props = {
  items: Sanity$GridItem[];
  stylingOptions: Sanity$GridStylingOptions;
};

const GridCards = ({ items, stylingOptions }: Props) => {
  const itemsPerRow = stegaClean(stylingOptions.itemsPerRow);
  const imageWidth = stegaClean(stylingOptions.imageWidth);
  const equalizeImageHeight = stegaClean(stylingOptions.equalizeImageHeight);

  const shouldShowCallToAction = (item: Sanity$GridItem) =>
    !!item.url && !!item.callToAction;
  return (
    <ul
      className={cx(
        'grid grid-cols-1 gap-8 md:grid-cols-2',
        `${itemsPerRow.medium === 2 && 'sm:grid-cols-2'}`,
        `${itemsPerRow.medium === 3 && 'sm:grid-cols-3'}`,
        `${itemsPerRow.medium === 4 && 'sm:grid-cols-4'}`,
        `${itemsPerRow.large === 2 && 'lg:grid-cols-2'}`,
        `${itemsPerRow.large === 3 && 'lg:grid-cols-3'}`,
        `${itemsPerRow.large === 4 && 'lg:grid-cols-4'}`,
      )}
    >
      {items.map((item) => {
        return (
          <li key={item._key} className="list-none">
            <ConditionalWrap
              condition={!!item.url}
              wrap={(children) => (
                <a
                  className={cx(
                    'block h-full rounded-lg no-underline',
                    'focus:outline-2 focus:outline-offset-4 focus:outline-blue-70 focus:outline-solid',
                  )}
                  href={item.url}
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              )}
            >
              <div className="relative flex h-full flex-col overflow-hidden rounded-lg">
                <div className={`${imageWidth !== 'large' && 'p-5'}`}>
                  <div className="relative mb-6 aspect-video">
                    <SanityImage
                      className="h-full rounded-xl"
                      image={item.image}
                      alt={item.imageAltText}
                      equalizeImageHeight={equalizeImageHeight}
                      fill
                      sizes="(max-width: 767px) 320px, 280px"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {item.heading && (
                    <h3 className="text-lg font-semibold text-balance text-subheading lg:text-xl">
                      {item.heading}
                    </h3>
                  )}
                  {item.description && (
                    <PortableText value={item.description} />
                  )}
                  {shouldShowCallToAction(item) && (
                    <p className="mt-4 font-semibold text-strong underline-offset-2 hover:underline">
                      {item.callToAction}{' '}
                      <ArrowRight className="mb-1 ml-1 inline-block" />
                    </p>
                  )}
                </div>
              </div>
            </ConditionalWrap>
          </li>
        );
      })}
    </ul>
  );
};

export default GridCards;
